import {
  faCaretDown,
  faCaretUp,
  faChevronLeft,
  faChevronRight,
  faChevronDoubleLeft,
  faChevronDoubleRight,
} from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import { Fragment, useEffect } from 'react';

import { ButtonSubtle, ButtonGroup } from 'Buttons';
import { Icon } from 'Icons';

import { usePrevious } from './hooks';

import './styles.css';
import '../Buttons/styles/buttonStyles.css';

/*
This table will accept the following custom hooks:
- usePaginationTable
- usePaginationTableWithSearch

From DT:
- noDataMessage: Needs to be customzied per table & translated

From client:
- i18n: functions for translation
*/
const PaginationTable = ({
  canNextPage,
  canPreviousPage,
  columnsLength,
  getTableBodyProps,
  getTableProps,
  gotoPage,
  handlePageSizeChange,
  headerGroups,
  i18n,
  nextPage,
  noDataMessage,
  page,
  pageCount,
  prepareRow,
  previousPage,
  rows,
  setPageSize,
  state: { pageIndex, pageSize },
}) => {
  const firstRowCount = pageIndex * pageSize + 1;
  const lastRowCount = pageIndex * pageSize + page.length;
  const totalItems = rows.length;
  const strings = {
    firstPage: i18n.firstPage(),
    lastPage: i18n.lastPage(),
    nextPage: i18n.nextPage(),
    pagePosition: i18n.pagePosition({
      firstRowCount,
      lastRowCount,
      totalItems,
    }),
    previousPage: i18n.previousPage(),
    showPageId: i18n.showPageId(),
    showPageLabel: i18n.showPageLabel(),
  };

  const previousPageSize = usePrevious(pageSize);

  useEffect(() => {
    if (previousPageSize && pageSize !== previousPageSize) {
      handlePageSizeChange(pageSize);
    }
  }, [handlePageSizeChange, pageSize, previousPageSize]);

  return (
    <Fragment>
      <table
        {...getTableProps()}
        className="w-full border-collapse text-md table-button-styles"
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => {
                const headerProps = column.canSort
                  ? column.getHeaderProps(column.getSortByToggleProps())
                  : column.getHeaderProps();
                return (
                  <th
                    {...headerProps}
                    className="uppercase text-left text-gray-5 border-b-2 border-gray-2 py-4 px-2"
                  >
                    <span className={column.canSort ? 'relative' : undefined}>
                      {column.render('Header')}
                      {column.isSorted && (
                        <span className="absolute -right-4 top-1/2 transform -translate-y-1/2">
                          {column.isSortedDesc ? (
                            <Icon icon={faCaretDown} />
                          ) : (
                            <Icon icon={faCaretUp} />
                          )}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.length ? (
            page.map((row) => {
              // prepareRow lazily initializes a row before knowing if it's rendered
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  className="border-b border-gray-1 hover:bg-gray-1"
                >
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()} className="py-4 px-2">
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          ) : (
            <tr className="text-center border-b border-gray-1">
              <td className="py-4 px-2" colSpan={columnsLength}>
                {noDataMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {rows.length > 0 && (
        <div className={'flex items-baseline justify-between p-4'}>
          <div className="max-w-[30%] min-w-60">
            <label className="sr-only" htmlFor={strings.showPageId}>
              {strings.showPageLabel}
            </label>
            <select
              id={strings.showPageId}
              name={strings.showPageId}
              // onBlur needed for screen readers
              onBlur={(e) => setPageSize(Number(e.target.value))}
              onChange={(e) => setPageSize(Number(e.target.value))}
              value={pageSize}
            >
              {[5, 10, 20, 30, 40, 50].map((size) => {
                const showPageTranslation = i18n.showPage({ size });
                return (
                  <option key={size} value={size}>
                    {showPageTranslation}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="flex items-center justify-end">
            <p className="mb-0 mr-4">{strings.pagePosition}</p>
            <ButtonGroup>
              <ButtonSubtle
                className="button-secondary dt-button"
                disabled={!canPreviousPage}
                onClick={() => gotoPage(0)}
              >
                <Icon icon={faChevronDoubleLeft} />
                <span className="sr-only">{strings.firstPage}</span>
              </ButtonSubtle>
              <ButtonSubtle
                className="button-secondary dt-button"
                disabled={!canPreviousPage}
                onClick={previousPage}
              >
                <Icon icon={faChevronLeft} />
                <span className="sr-only">{strings.previousPage}</span>
              </ButtonSubtle>
              <ButtonSubtle
                className="button-secondary dt-button"
                disabled={!canNextPage}
                onClick={nextPage}
              >
                <Icon icon={faChevronRight} />
                <span className="sr-only">{strings.nextPage}</span>
              </ButtonSubtle>
              <ButtonSubtle
                className="button-secondary dt-button"
                disabled={!canNextPage}
                onClick={() => gotoPage(pageCount - 1)}
              >
                <Icon icon={faChevronDoubleRight} />
                <span className="sr-only">{strings.lastPage}</span>
              </ButtonSubtle>
            </ButtonGroup>
          </div>
        </div>
      )}
    </Fragment>
  );
};

PaginationTable.defaultProps = {
  handlePageSizeChange: () => {},
  rows: [],
};

PaginationTable.propTypes = {
  canNextPage: PropTypes.bool.isRequired,
  canPreviousPage: PropTypes.bool.isRequired,
  columnsLength: PropTypes.number.isRequired,
  getTableBodyProps: PropTypes.func.isRequired,
  getTableProps: PropTypes.func.isRequired,
  gotoPage: PropTypes.func.isRequired,
  handlePageSizeChange: PropTypes.func,
  headerGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  i18n: PropTypes.shape({
    firstPage: PropTypes.func.isRequired,
    lastPage: PropTypes.func.isRequired,
    nextPage: PropTypes.func.isRequired,
    pagePosition: PropTypes.func.isRequired,
    previousPage: PropTypes.func.isRequired,
    showPage: PropTypes.func.isRequired,
    showPageId: PropTypes.func.isRequired,
    showPageLabel: PropTypes.func.isRequired,
  }).isRequired,
  nextPage: PropTypes.func.isRequired,
  noDataMessage: PropTypes.string.isRequired,
  page: PropTypes.arrayOf(PropTypes.object).isRequired,
  pageCount: PropTypes.number.isRequired,
  prepareRow: PropTypes.func.isRequired,
  previousPage: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  setPageSize: PropTypes.func.isRequired,
  state: PropTypes.shape({
    pageIndex: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
  }).isRequired,
};

export default PaginationTable;
